import React from "react"
import { twMerge } from "tailwind-merge"

import placeholderImage from "../../../../assets/images/profile-photo-placeholder.jpg"
import { formatDurationWithSessions } from "../../../utils/utils"
import AnimatedSection from "../../ProProfileShared/AnimatedSection"
import { generateColorPalette } from "../utils/enhancedColorUtils"
import { applyButtonTheme, getButtonClasses } from "../utils/themeUtils"
import { buildPublicPageUrl, buildServiceUrl, buildBookingUrl } from "../utils/urlUtils"
import { DEFAULT_HIGHLIGHT_CLASSES } from "../WebsiteBuilderV2Page"

const PackagesSectionMobile = ({ section, practice, theme, highlightedField, pageSlug }) => {
  const isHighlighted = highlightedField === `section-${section.id}`
  const settings = section.settings || {}
  const layout = settings.layout || "list"
  const showCTA = settings.showCTA || true
  const ctaText = settings.ctaText || "Browse Packages"
  const ctaAction = settings.ctaAction || "page"
  const ctaTarget = settings.ctaTarget || "packages"
  const selectedPackageIds = settings.selectedPackageIds || []

  // Get theme colors with fallback generation for bgMedium and bgExtraLight
  const getThemeColors = () => {
    if (!theme?.colors) return { bgLight: "#FFFFFF", bgMedium: "#E6DAD7", bgExtraLight: "#F2EEEC" }

    const colors = { ...theme.colors }

    // If bgMedium or bgExtraLight are missing, generate them
    if (!colors.bgMedium || !colors.bgExtraLight) {
      const primaryColor = colors.primary || "#BD654F"
      const generated = generateColorPalette(primaryColor)
      colors.bgMedium = colors.bgMedium || generated.bgMedium
      colors.bgExtraLight = colors.bgExtraLight || generated.bgExtraLight
      colors.bgLight = colors.bgLight || generated.bgLight
    }

    return colors
  }

  const themeColors = getThemeColors()
  const sectionBackgroundColor = themeColors.bgLight || "#FFFFFF"

  const handleCTAClick = () => {
    switch (ctaAction) {
      case "page":
        if (ctaTarget) {
          window.location.href = buildPublicPageUrl(ctaTarget, practice)
        }
        break
      case "service":
        if (ctaTarget) {
          window.open(buildServiceUrl(ctaTarget, practice), "_blank")
        }
        break
      case "booking":
        window.open(buildBookingUrl(practice), "_blank")
        break
      case "contact":
        document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
        break
      case "scroll":
        window.scrollBy({ top: window.innerHeight, behavior: "smooth" })
        break
      case "external":
        break
      default:
        break
    }
  }

  const handlePackageClick = (packageId) => {
    window.location.href = buildServiceUrl(packageId, practice)
  }

  // Get practice packages
  const allPracticePackages = practice?.packages || []

  // If specific packages are selected, use those; otherwise use all packages
  const selectedPackages =
    selectedPackageIds.length > 0
      ? allPracticePackages.filter((pkg) => selectedPackageIds.includes(pkg.id))
      : allPracticePackages

  // Combine packages, limit based on layout and page
  const isHomePage = pageSlug === "home"
  const maxItems = isHomePage
    ? layout === "highlights_2"
      ? 2
      : layout === "highlights_3"
      ? 3
      : 3 // Limit to 3 for list layout when on home page
    : 999 // Show all items on non-home pages
  const allItems = selectedPackages.slice(0, maxItems)
  const hasMorePackages = selectedPackages.length > maxItems

  const formatPrice = (amountCents, currency) => {
    if (amountCents === 0) {
      return "Free"
    }
    const amount = amountCents / 100
    return `$${amount.toFixed(2)}${currency === "CAD" ? " CAD" : ""}`
  }

  // Render package card for mobile (vertical stack with image on top)
  const renderMobilePackageCard = (item, index) => {
    const price = item.amountCents ? formatPrice(item.amountCents, practice?.defaultCurrency || "USD") : ""
    const duration = item.timeLength ? formatDurationWithSessions(item.timeLength, item) : ""

    // Format location and service type information
    const serviceType = item.virtual ? "Virtual" : "In-person"
    const location = practice?.location?.city ? `${practice.location.city}, ${practice.location.state}` : ""
    const locationType = item.atHome || item.at_home ? "At your home" : ""
    const locationInfo = [serviceType, location, locationType].filter(Boolean).join(" • ")

    // Get image URL from various possible sources
    const imageUrl =
      item.imageUrl ||
      item.mediumWebpUrl ||
      (item.photoHandles?.large?.webp ? `https://cdn.filestackcontent.com/${item.photoHandles.large.webp}` : null) ||
      (item.photoHandles?.original?.any ? `https://cdn.filestackcontent.com/${item.photoHandles.original.any}` : null)

    // Truncate description for display
    const displayDescription =
      item.description && item.description.length > 200 ? item.description.substring(0, 200) + "..." : item.description

    return (
      <div key={index} className="flex w-full flex-col items-start justify-start gap-4">
        {/* Image - Full width with aspect ratio */}
        <div className="aspect-[396/250] w-full overflow-hidden bg-gray-100">
          <img
            src={imageUrl || placeholderImage}
            alt={item.name}
            className="h-full w-full object-cover"
            onError={(e) => {
              if (e.target.src !== placeholderImage) e.target.src = placeholderImage
            }}
          />
        </div>

        {/* Content */}
        <div className="flex w-full flex-col items-start justify-start gap-2">
          {/* Price and Title */}
          <div className="flex w-full flex-col items-start justify-start">
            {price && (
              <div
                className="text-[18px] font-semibold leading-[24px]"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  fontWeight: theme?.fonts?.paragraphBoldWeight || "600",
                  color: theme?.colors?.textDark || "#55433F"
                }}>
                {price}
              </div>
            )}
            <div
              className="line-clamp-2 text-[22px] font-bold leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: theme?.colors?.textDark || "#000000",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden"
              }}>
              {item.name}
            </div>
          </div>

          {/* Package Badge */}
          <span
            className="rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider"
            style={{
              backgroundColor: theme?.colors?.primary || "#BD654F",
              color: "#FFFFFF",
              fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
            }}>
            {item.numberOfSessions ? `${item.numberOfSessions} Session Package` : "Package"}
          </span>

          {/* Duration and Location */}
          {(duration || locationInfo) && (
            <div
              className="text-[14px] font-normal leading-[20px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.textDark || "#55433F"
              }}>
              {duration && locationInfo ? `${duration} • ${locationInfo}` : duration || locationInfo}
            </div>
          )}

          {/* Description */}
          {displayDescription && (
            <div
              className="prose prose-lg max-w-none text-[16px] font-normal leading-[24px]"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.textDark || "#000000"
              }}
              dangerouslySetInnerHTML={{ __html: displayDescription }}></div>
          )}

          {/* Details button */}
          <div
            className="flex w-full cursor-pointer items-center justify-center gap-3 px-10 py-3 transition-opacity hover:opacity-90"
            style={{
              backgroundColor: theme?.colors?.primary || "#BD654F",
              borderRadius: theme?.borderRadius || "12px"
            }}
            onClick={() => handlePackageClick(item.id)}>
            <div
              className="text-[16px] font-normal leading-[24px] text-white"
              style={{
                fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                color: theme?.colors?.bgLight || "#F4ECE9"
              }}>
              Details
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <section
      className={twMerge("relative py-8 pb-[72px]", isHighlighted ? DEFAULT_HIGHLIGHT_CLASSES : "")}
      style={{
        backgroundColor: sectionBackgroundColor
      }}>
      <AnimatedSection className="mx-auto max-w-[1160px] px-5">
        <div className="flex w-full flex-col items-start gap-16">
          {/* Header */}
          <div className="flex w-full flex-col items-start gap-8">
            <h2
              className="w-full text-left text-[28px] font-bold leading-[32px]"
              style={{
                fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                fontWeight: theme?.fonts?.headingWeight || "700",
                color: theme?.colors?.textDark || "#55433F"
              }}>
              {section.headline || "Packages"}
            </h2>
            {section.body && (
              <p
                className="max-w-3xl text-left text-[18px] leading-[24px]"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                  fontWeight: theme?.fonts?.paragraphRegularWeight || "400",
                  color: theme?.colors?.textDark || "#55433F"
                }}>
                {section.body}
              </p>
            )}
          </div>

          {/* Packages Grid */}
          {allItems.length > 0 && (
            <div className="w-full">
              {layout === "highlights_2" || layout === "highlights_3" ? (
                // Highlights layout - grid for mobile
                <div
                  className={`grid gap-6 ${layout === "highlights_2" ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
                  {allItems.map((item, index) => renderMobilePackageCard(item, index))}
                </div>
              ) : (
                // List layout - vertical stack
                <div className="flex flex-col gap-10">
                  {allItems.map((item, index) => renderMobilePackageCard(item, index))}
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {allItems.length === 0 && (
            <div className="flex flex-col items-center gap-6 py-16 text-center">
              <div className="text-6xl">📦</div>
              <h3
                className="text-2xl font-semibold"
                style={{
                  fontFamily: theme?.fonts?.heading || "Playfair Display, serif",
                  color: theme?.colors?.textDark || "#55433F"
                }}>
                No packages available
              </h3>
              <p
                className="text-lg text-gray-600"
                style={{
                  fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif"
                }}>
                {practice?.packages?.length === 0
                  ? "This practice hasn't added any packages yet."
                  : "No packages match the current selection."}
              </p>
            </div>
          )}

          {/* Main CTA Button - Only show if not on packages page */}
          {showCTA && allItems.length > 0 && pageSlug !== "packages" && (
            <div className="flex justify-center pt-4">
              {ctaAction === "booking" ? (
                <button
                  onClick={() => (window.location.href = buildPublicPageUrl("packages", practice))}
                  className={twMerge(
                    "inline-flex items-center justify-center px-10 py-3 text-[16px] font-semibold text-white transition-all duration-300 hover:shadow-lg",
                    getButtonClasses(theme?.buttonStyle)
                  )}
                  style={applyButtonTheme(
                    {
                      backgroundColor: theme?.colors?.primary || "#BD654F",
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      borderRadius: theme?.borderRadius || "12px"
                    },
                    theme
                  )}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                    e.target.style.transform = "translateY(-2px)"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                    e.target.style.transform = "translateY(0)"
                  }}>
                  {ctaText}
                </button>
              ) : (
                <button
                  onClick={handleCTAClick}
                  className={twMerge(
                    "inline-flex items-center justify-center px-10 py-3 text-[16px] font-semibold text-white transition-all duration-300 hover:shadow-lg",
                    getButtonClasses(theme?.buttonStyle)
                  )}
                  style={applyButtonTheme(
                    {
                      backgroundColor: theme?.colors?.primary || "#BD654F",
                      fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                      borderRadius: theme?.borderRadius || "12px"
                    },
                    theme
                  )}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                    e.target.style.transform = "translateY(-2px)"
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                    e.target.style.transform = "translateY(0)"
                  }}>
                  {ctaText}
                </button>
              )}
            </div>
          )}

          {/* Show More Button for non-home pages */}
          {hasMorePackages && !isHomePage && (
            <div className="mt-8 text-center">
              <button
                onClick={() => (window.location.href = buildPublicPageUrl("services", practice))}
                className="rounded-lg px-8 py-3 font-medium text-white transition-colors"
                style={applyButtonTheme(
                  {
                    backgroundColor: theme?.colors?.primary || "#BD654F",
                    fontFamily: theme?.fonts?.paragraph || "Work Sans, sans-serif",
                    borderRadius: theme?.borderRadius || "12px"
                  },
                  theme
                )}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primaryDark || "#A55A45"
                  e.target.style.transform = "translateY(-2px)"
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = theme?.colors?.primary || "#BD654F"
                  e.target.style.transform = "translateY(0)"
                }}>
                View All Packages
              </button>
            </div>
          )}
        </div>
      </AnimatedSection>
    </section>
  )
}

export default PackagesSectionMobile
